"use client"

import { FormikProvider, useFormik, useFormikContext } from "formik"
import { AlertCircle, Loader2 } from "lucide-react"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormInput, FormSelect } from "@/components/form-inputs"
import { WebsiteUrlFormInput } from "@/components/ai/knowledge-base/website-url-form-input"
import { useImportKnowledgeBaseFromUrlMutation } from "@/hooks/mutations/knowledge-base-url-import"
import type { ImportKnowledgeBaseFromUrlInput, ImportKnowledgeBaseFromUrlResult } from "@/server-actions/knowledge-base-url-import"
import type { KnowledgeBaseFolderView } from "@/server-actions/knowledge-base-folders"
import { useSyncKnowledgeBaseFolderMutation } from "@/hooks/mutations/knowledge-base-folders"
import {
  KB_URL_IMPORT_MAX_PAGES,
} from "@/lib/ai/knowledge-base/url-import/constants"
import {
  getUrlImportDialogInitialValues,
  type UrlImportFormValues,
} from "@/lib/ai/knowledge-base/url-import/dialog-config"
import {
  isValidWebsiteUrl,
  normalizeWebsiteUrlInput,
} from "@/lib/ai/knowledge-base/url-import/url"

type ImportFromUrlDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  maxPagesLimit?: number
  resyncFolder?: KnowledgeBaseFolderView | null
  onImported?: (result: ImportKnowledgeBaseFromUrlResult) => void | Promise<void>
}

const INJECTION_MODE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "direct", label: "Direct" },
  { value: "rag", label: "RAG" },
] as const

function toImportInput(values: UrlImportFormValues): ImportKnowledgeBaseFromUrlInput {
  return {
    url: normalizeWebsiteUrlInput(values.url),
    category: values.category || undefined,
    tags: values.tags
      .split(",")
      .map(tag => tag.trim())
      .filter(Boolean),
    injectionMode: values.injectionMode,
    isDefault: values.isDefault,
    maxPages: values.maxPages,
    maxDepth: values.maxDepth,
  }
}

function validate(values: UrlImportFormValues, maxPagesLimit: number) {
  const errors: Partial<Record<keyof UrlImportFormValues, string>> = {}

  if (!values.url.trim()) {
    errors.url = "Website URL is required."
  } else if (!isValidWebsiteUrl(values.url)) {
    errors.url = "Enter a valid URL."
  }

  if (maxPagesLimit < 1) {
    errors.maxPages = "Your plan has no available knowledge-base document slots."
  } else if (!Number.isInteger(values.maxPages) || values.maxPages < 1 || values.maxPages > maxPagesLimit) {
    errors.maxPages = `Max pages must be between 1 and ${maxPagesLimit}.`
  }

  if (!Number.isInteger(values.maxDepth) || values.maxDepth < 0 || values.maxDepth > 5) {
    errors.maxDepth = "Max depth must be between 0 and 5."
  }

  return errors
}

function NumberField({
  name,
  label,
  min,
  max,
}: {
  name: "maxPages" | "maxDepth"
  label: string
  min: number
  max: number
}) {
  const { values, errors, touched, setFieldTouched, setFieldValue } = useFormikContext<UrlImportFormValues>()
  const error = touched[name] && errors[name] ? errors[name] : null

  return (
    <div className="space-y-1.5">
      <Label htmlFor={`kb-import-${name}`}>{label}</Label>
      <Input
        id={`kb-import-${name}`}
        type="number"
        min={min}
        max={max}
        value={values[name]}
        aria-invalid={Boolean(error)}
        onBlur={() => {
          void setFieldTouched(name, true, true)
        }}
        onChange={(event) => {
          void setFieldValue(name, Number(event.target.value))
        }}
      />
      {error ? <p className="text-xs font-medium text-destructive">{error}</p> : null}
    </div>
  )
}

function DefaultKnowledgeBaseField() {
  const { values, setFieldTouched, setFieldValue } = useFormikContext<UrlImportFormValues>()

  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
      <Checkbox
        checked={values.isDefault}
        onCheckedChange={(checked) => {
          void setFieldValue("isDefault", checked === true)
          void setFieldTouched("isDefault", true, false)
        }}
      />
      <span className="space-y-1">
        <span className="block text-sm font-medium">Apply imported pages to all agents</span>
        <span className="block text-xs text-muted-foreground">
          New documents become default knowledge bases and voice agents are queued for ElevenLabs sync.
        </span>
      </span>
    </label>
  )
}

export function ImportFromUrlDialog({
  open,
  onOpenChange,
  maxPagesLimit = KB_URL_IMPORT_MAX_PAGES,
  resyncFolder,
  onImported,
}: ImportFromUrlDialogProps) {
  const normalizedMaxPagesLimit = Math.max(0, Math.min(maxPagesLimit, KB_URL_IMPORT_MAX_PAGES))
  const handleSuccess = (result: ImportKnowledgeBaseFromUrlResult) => {
    onOpenChange(false)
    formik.resetForm()
    void Promise.resolve(onImported?.(result)).catch((error) => {
      console.error("[Knowledge Base] Failed to refresh after URL import:", error)
    })
  }
  const importFromUrlMutation = useImportKnowledgeBaseFromUrlMutation({ onSuccess: handleSuccess })
  const resyncFolderMutation = useSyncKnowledgeBaseFolderMutation({ onSuccess: handleSuccess })

  const formik = useFormik<UrlImportFormValues>({
    initialValues: getUrlImportDialogInitialValues(normalizedMaxPagesLimit, resyncFolder),
    enableReinitialize: true,
    validate: (values) => validate(values, normalizedMaxPagesLimit),
    onSubmit: async (values, helpers) => {
      try {
        const importInput = toImportInput(values)
        const setErrors = helpers.setErrors as (errors: Record<string, unknown>) => void
        if (resyncFolder) {
          const { url: _url, ...settings } = importInput
          await resyncFolderMutation.mutateAsync({
            folderId: resyncFolder.id,
            syncScope: "discover",
            settings,
            setErrors,
          })
        } else {
          await importFromUrlMutation.mutateAsync({ ...importInput, setErrors })
        }
      } catch {
        // withQueryHandling already shows the error toast and maps backend validation errors.
      }
    },
  })

  const isSubmitting = importFromUrlMutation.isPending || resyncFolderMutation.isPending
  const hasImportCapacity = normalizedMaxPagesLimit > 0
  const isDiscoveryResync = Boolean(resyncFolder)

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isSubmitting) {
          formik.resetForm()
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{isDiscoveryResync ? "Resync and pull new pages" : "Import from URL"}</DialogTitle>
              <DialogDescription>
                {isDiscoveryResync
                  ? "Crawl this website again, update changed pages, and import newly discovered pages."
                  : "Crawl a website recursively and import each discovered page as a markdown knowledge-base document."}
              </DialogDescription>
            </DialogHeader>

            <WebsiteUrlFormInput placeholder="example.com/docs" disabled={isDiscoveryResync} />

            {!hasImportCapacity && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Your plan has no knowledge-base document slots available. Upgrade your plan or delete documents
                  before importing from a URL.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <FormInput name="category" label="Category" />
              <FormInput name="tags" label="Tags" placeholder="docs, website" />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <FormSelect name="injectionMode" label="Mode" options={INJECTION_MODE_OPTIONS} />
              <NumberField
                name="maxPages"
                label={isDiscoveryResync ? "Max new pages" : "Max pages"}
                min={1}
                max={Math.max(1, normalizedMaxPagesLimit)}
              />
              <NumberField name="maxDepth" label="Max depth" min={0} max={5} />
            </div>
            <p className="text-xs text-muted-foreground">
              <span>Available:</span> {normalizedMaxPagesLimit} <span>page</span><span>{normalizedMaxPagesLimit === 1 ? "" : "s"}</span> <span>for this import.</span>
            </p>

            <DefaultKnowledgeBaseField />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !hasImportCapacity || !formik.values.url.trim()}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <span>{isDiscoveryResync ? "Start resync" : "Import"}</span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}
